/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback } from "react";
import { DocumentationView } from "../DocumentationView";

export interface IMdxLinkProps {
	href?: string;
	children?: React.ReactNode;
	view: DocumentationView;
}

/**
 * Checks if a URL is an external/absolute link
 */
function isExternalLink(href: string): boolean {
	// Absolute URLs (http://, https://, //, etc.)
	if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith("//")) {
		return true;
	}

	// Mailto, tel, etc.
	if (/^(mailto|tel):/i.test(href)) {
		return true;
	}

	return false;
}

/**
 * Checks if a URL is an anchor-only link
 */
function isAnchorLink(href: string): boolean {
	return href.startsWith("#");
}

/**
 * Extracts documentation path from a relative link
 * Handles paths like "guides/View.mdx", "./guides/View.mdx", "../guides/View.md"
 * Returns normalized path without extension (e.g., "guides/View")
 */
function extractDocPath(href: string, currentPath: string | null): string {
	// Remove leading ./
	let path = href.replace(/^\.\//, "");

	// Handle relative paths with ../
	if (path.startsWith("../") && currentPath) {
		const currentParts = currentPath.split("/");
		currentParts.pop(); // Remove current file name

		while (path.startsWith("../")) {
			path = path.substring(3);
			currentParts.pop();
		}

		if (currentParts.length > 0) {
			path = currentParts.join("/") + "/" + path;
		}
	}

	// Remove .md or .mdx extension
	path = path.replace(/\.mdx?$/i, "");

	return path;
}

/**
 * Scrolls to an element by its ID
 */
function scrollToAnchor(anchor: string): void {
	const id = anchor.substring(1); // Remove the # prefix
	const element = document.getElementById(id);

	if (element) {
		element.scrollIntoView({ behavior: "smooth", block: "start" });
	}
}

export const MdxLink: FC<IMdxLinkProps> = ({ href, children, view }) => {
	const isRelative = href && !isExternalLink(href) && !isAnchorLink(href);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			if (!href) {
				return;
			}

			// Handle anchor links - scroll to element
			if (isAnchorLink(href)) {
				e.preventDefault();
				scrollToAnchor(href);
				return;
			}

			// Handle external links - open in new window
			if (isExternalLink(href)) {
				e.preventDefault();
				window.open(href, "_blank", "noopener,noreferrer");
				return;
			}

			// Handle relative/internal links - navigate within docs
			e.preventDefault();
			const docPath = extractDocPath(href, view.getSelected());

			// Ctrl+click (Windows/Linux) or Cmd+click (Mac) opens in new tab
			const openInNewTab = e.ctrlKey || e.metaKey;

			if (openInNewTab) {
				// Open in new documentation tab via command
				view.openDocumentationInNewTab(docPath);
			} else {
				// Navigate in current view
				view.setSelected(docPath);
			}
		},
		[ href, view ]
	);

	// For relative links, show # to indicate local navigation
	const displayHref = isRelative ? "#page:" + href : href;

	return (
		<a href={displayHref} onClick={handleClick}>
			{children}
		</a>
	);
};
